import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import type { UserProfile, AdminStats, AdminUserFilters, PaginatedResponse, ApiSuccess, AccountRequest, AccountRequestStatus, AdminCreateUserRequest } from '@spi/shared';

export function useAdminUsers(filters: AdminUserFilters = {}) {
  const params = new URLSearchParams();
  if (filters.role) params.set('role', filters.role);
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () => apiFetch<PaginatedResponse<UserProfile>>(`/api/admin/users?${params}`),
  });
}

export function useAdminUser(id: number) {
  return useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => apiFetch<UserProfile>(`/api/admin/users/${id}`),
    enabled: id > 0,
  });
}

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => apiFetch<AdminStats>('/api/admin/stats'),
  });
}

export function useApproveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/users/${id}/approve`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useRejectUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/users/${id}/reject`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/users/${id}/suspend`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

// Seed data hooks
interface SeedDataStatus {
  users: number;
  projects: number;
  asks: number;
  transactions: number;
}

export function useSeedDataStatus() {
  return useQuery({
    queryKey: ['admin', 'seed-data'],
    queryFn: () => apiFetch<SeedDataStatus>('/api/admin/seed-data'),
  });
}

export function useGenerateSeedData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SeedDataStatus>('/api/admin/seed-data', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteSeedData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ApiSuccess>('/api/admin/seed-data', { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// Admin create user
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminCreateUserRequest) =>
      apiFetch<UserProfile>('/api/admin/users', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

// Account requests
export function useAccountRequests(filters: { status?: AccountRequestStatus; page?: number; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery({
    queryKey: ['admin', 'account-requests', filters],
    queryFn: () => apiFetch<PaginatedResponse<AccountRequest>>(`/api/admin/account-requests?${params}`),
  });
}

export function usePendingAccountRequestsCount() {
  return useQuery({
    queryKey: ['admin', 'account-requests', { status: 'pending', limit: 1 }],
    queryFn: () => apiFetch<PaginatedResponse<AccountRequest>>('/api/admin/account-requests?status=pending&limit=1'),
    select: (data) => data.total,
  });
}

export function useDismissAccountRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/account-requests/${id}/dismiss`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useApproveAccountRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/account-requests/${id}/approve`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

// Contact submissions
export interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  organization: string | null;
  message: string;
  created_at: string;
}

export function useContactSubmissions(page = 1) {
  return useQuery({
    queryKey: ['admin', 'contacts', page],
    queryFn: () => apiFetch<PaginatedResponse<ContactSubmission>>(`/api/contact/submissions?page=${page}&limit=20`),
  });
}
